import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { DownloadBox } from "@/atoms";
import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { exportTracksMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "@/utils";

const useExportTracks = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportTracksAction, { loading }] = useMutation(exportTracksMutation);

    const { exportTracksPending: exportTracksPendingToastName, exportTracksSuccess: exportTracksSuccessToastName } =
        namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportTracks = async (ids) => {
        setExporting(true);

        try {
            await exportTracksAction({
                onCompleted: (data) => {
                    setExporting(false);

                    const randomId = getUniqueRandomId();

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportTracksSuccessToastName}_${toastId}_${randomId}`}
                                link={data.exportTracks}
                                message={t("messages.downloadMsgs.download", { entity: t("tracks.name") })}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportTracksSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(
                        openAlert(
                            t("messages.exportMsgs.success", { entity: t("tracks.name") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    toCsv: true,
                    tracksIds: ids,
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportTracksPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(() => t("messages.exportMsgs.pending", { entity: t("tracks.name") }), {
                    toastId: `${exportTracksPendingToastName}_${toastId}`,
                });
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportTracks };
};

export default useExportTracks;
